import { clampTransitionDuration, TRANSITION_TYPES } from '@/lib/editor/transitions';
import { VIDEO_FILTERS } from '@/lib/editor/filters';
import { cleanLayerTransition } from '@/lib/editor/layerTransitions';
import { cleanLayerKeyframes } from '@/lib/editor/layerKeyframes';
import {
  clipFit,
  clipOpacity,
  clipPlaybackRate,
  clipPositionX,
  clipPositionY,
  clipRotation,
  clipScale,
} from '@/lib/editor/clipProperties';
import { cleanTrackSettings } from '@/lib/editor/trackSettings';
import type {
  EditorAspectRatio,
  EditorAudioTrack,
  EditorExportOptions,
  GenJobState,
  GenProposal,
  EditorOverlayLayer,
  EditorProjectSavePayload,
  EditorRenderCanvas,
  EditorRenderSpec,
  EditorStickerAsset,
  EditorTimelineClip,
  EditorTransition,
} from '@/types/editor';

export const EDITOR_RENDER_VERSION = 1 as const;
export const EDITOR_RENDER_FPS = 30;
export const EDITOR_RENDER_CANVAS: Record<EditorAspectRatio, EditorRenderCanvas> = {
  '9:16': { width: 1080, height: 1920, fps: EDITOR_RENDER_FPS, aspectRatio: '9:16' },
  '16:9': { width: 1920, height: 1080, fps: EDITOR_RENDER_FPS, aspectRatio: '16:9' },
  '1:1': { width: 1080, height: 1080, fps: EDITOR_RENDER_FPS, aspectRatio: '1:1' },
};

const EXPORT_LONG_EDGE: Record<EditorExportOptions['resolution'], number> = {
  '720p': 1280,
  '1080p': 1920,
  '4k': 3840,
};

export function cleanEditorExportOptions(value: unknown): EditorExportOptions {
  const source = value && typeof value === 'object' ? value as Partial<EditorExportOptions> : {};
  const resolution = source.resolution === '720p' || source.resolution === '4k' ? source.resolution : '1080p';
  const fps = source.fps === 24 || source.fps === 60 ? source.fps : 30;
  return { resolution, fps };
}

export function editorRenderCanvas(
  aspectRatio: EditorAspectRatio,
  options?: Partial<EditorExportOptions>
): EditorRenderCanvas {
  const clean = cleanEditorExportOptions(options);
  const longEdge = EXPORT_LONG_EDGE[clean.resolution];
  const shortEdge = Math.round(longEdge * (9 / 16));
  const dimensions = aspectRatio === '9:16'
    ? { width: shortEdge, height: longEdge }
    : aspectRatio === '16:9'
      ? { width: longEdge, height: shortEdge }
      : { width: shortEdge, height: shortEdge };
  return { ...dimensions, fps: clean.fps, aspectRatio };
}

export function isDurableEditorUrl(src?: string) {
  return Boolean(src && !src.startsWith('blob:'));
}

export function cleanEditorTitle(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, 120) : 'Untitled edit';
}

export function cleanEditorAspectRatio(value: unknown): EditorAspectRatio {
  return value === '16:9' || value === '1:1' ? value : '9:16';
}

const VALID_FILTER_IDS = new Set(VIDEO_FILTERS.map((preset) => preset.id));

/** Validate an untrusted global filter preset id — unknown/missing → 'none'. */
export function cleanEditorFilterId(value: unknown): string {
  return typeof value === 'string' && VALID_FILTER_IDS.has(value) ? value : 'none';
}

export function cleanEditorDuration(value: unknown) {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return 0;
  return Math.min(duration, 900);
}

export function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Validate/clamp an untrusted audio track. A track needs an `s3Key`
 * (uploaded) OR a `src` (bundled library track or direct URL); missing both
 * drops the whole track, same as the old s3Key-only contract.
 */
export function cleanAudioTrack(value: unknown): EditorAudioTrack | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Partial<EditorAudioTrack>;
  const s3Key = typeof v.s3Key === 'string' && v.s3Key ? v.s3Key : undefined;
  const src = typeof v.src === 'string' && v.src ? v.src : undefined;
  if (!s3Key && !src) return undefined;
  return {
    ...(typeof v.id === 'string' && v.id ? { id: v.id.slice(0, 120) } : {}),
    ...(s3Key ? { s3Key } : {}),
    ...(src ? { src } : {}),
    volume: clampNumber(v.volume, 0, 1, 0.5),
    fadeIn: clampNumber(v.fadeIn, 0, 10, 0),
    fadeOut: clampNumber(v.fadeOut, 0, 10, 0),
    fileName: typeof v.fileName === 'string' ? v.fileName.slice(0, 200) : undefined,
    duration: typeof v.duration === 'number' && Number.isFinite(v.duration) ? v.duration : undefined,
    ...(v.start !== undefined ? { start: clampNumber(v.start, 0, 900, 0) } : {}),
    ...(v.timelineDuration !== undefined
      ? { timelineDuration: clampNumber(v.timelineDuration, 0.1, 900, Math.max(0.1, v.duration ?? 30)) }
      : {}),
    ...(v.sourceOffset !== undefined ? { sourceOffset: clampNumber(v.sourceOffset, 0, 900, 0) } : {}),
    ...(v.ducking ? { ducking: true } : {}),
    ...(v.muted ? { muted: true } : {}),
    ...(v.solo ? { solo: true } : {}),
    ...(v.pan !== undefined ? { pan: clampNumber(v.pan, -1, 1, 0) } : {}),
  };
}

export function cleanAudioTracks(value: unknown, legacy?: unknown): EditorAudioTrack[] {
  const source = Array.isArray(value) && value.length > 0 ? value : legacy ? [legacy] : [];
  const ids = new Set<string>();
  return source.flatMap((track, index) => {
    const clean = cleanAudioTrack(track);
    if (!clean) return [];
    let id = clean.id || `audio-${index + 1}`;
    while (ids.has(id)) id = `${id}-${index + 1}`;
    ids.add(id);
    return [{ ...clean, id }];
  }).slice(0, 16);
}

export function cleanEditorArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value.filter((item) => item && typeof item === 'object') : [];
}

const GEN_JOB_STATES = new Set<GenJobState>([
  'quoting', 'awaiting', 'submitting', 'running', 'completed', 'error', 'declined',
]);

/**
 * Sanitize durable Canny generation tasks. They are orchestration state, not
 * part of the render spec, but live on the project so running provider jobs
 * can resume polling after a reload.
 */
export function cleanGenProposals(value: unknown): GenProposal[] {
  if (!Array.isArray(value)) return [];
  return value.slice(-20).flatMap((rawProposal) => {
    if (!rawProposal || typeof rawProposal !== 'object') return [];
    const proposal = rawProposal as Partial<GenProposal>;
    if (typeof proposal.toolCallId !== 'string' || !proposal.toolCallId.trim()) return [];
    if (!Array.isArray(proposal.jobs) || proposal.jobs.length === 0) return [];

    const jobs = proposal.jobs.slice(0, 20).flatMap((rawJob) => {
      if (!rawJob || typeof rawJob !== 'object') return [];
      const job = rawJob as GenProposal['jobs'][number];
      const shot = job.shot;
      if (
        typeof job.localId !== 'string' || !job.localId ||
        !shot || typeof shot !== 'object' ||
        typeof shot.prompt !== 'string' || !shot.prompt.trim() ||
        typeof shot.description !== 'string' || !shot.description.trim() ||
        (shot.kind !== 'video' && shot.kind !== 'image')
      ) return [];
      const persistedState = GEN_JOB_STATES.has(job.state) ? job.state : 'error';
      // A job with no jobId never reached the server, so a pre-submit state
      // (quoting/submitting, or awaiting whose quote never landed) can't recover
      // after a reload — createGenProposal is idempotent by toolCallId and won't
      // re-fire. Downgrade to error so the card isn't stuck on the skeleton.
      const hasCost = typeof job.costUsd === 'number' && Number.isFinite(job.costUsd);
      const stuckPreSubmit =
        !job.jobId &&
        (persistedState === 'submitting' ||
          persistedState === 'quoting' ||
          (persistedState === 'awaiting' && !hasCost));
      const state: GenJobState = stuckPreSubmit ? 'error' : persistedState;
      return [{
        localId: job.localId.slice(0, 160),
        shot: {
          order: Math.max(0, Math.floor(Number(shot.order) || 0)),
          description: shot.description.slice(0, 500),
          kind: shot.kind,
          prompt: shot.prompt.slice(0, 4000),
          ...(shot.useReferenceImage ? { useReferenceImage: true } : {}),
          ...(shot.durationSec != null ? { durationSec: clampNumber(shot.durationSec, 1, 30, 6) } : {}),
          ...(shot.aspectRatio === '16:9' || shot.aspectRatio === '1:1' || shot.aspectRatio === '9:16'
            ? { aspectRatio: shot.aspectRatio }
            : {}),
        },
        state,
        ...(typeof job.modelLabel === 'string' ? { modelLabel: job.modelLabel.slice(0, 160) } : {}),
        ...(typeof job.jobId === 'string' ? { jobId: job.jobId.slice(0, 240) } : {}),
        ...(typeof job.costUsd === 'number' && Number.isFinite(job.costUsd) ? { costUsd: Math.max(0, job.costUsd) } : {}),
        ...(typeof job.src === 'string' && /^https?:\/\//.test(job.src) ? { src: job.src.slice(0, 4000) } : {}),
        ...(typeof job.error === 'string'
          ? { error: job.error.slice(0, 500) }
          : state === 'error'
            ? { error: 'Задача не успела запуститься до перезагрузки' }
            : {}),
      }];
    });
    if (jobs.length === 0) return [];

    return [{
      toolCallId: proposal.toolCallId.slice(0, 200),
      summary: typeof proposal.summary === 'string' ? proposal.summary.slice(0, 1000) : 'Генерация',
      jobs,
      referenceUrl: typeof proposal.referenceUrl === 'string' && /^https?:\/\//.test(proposal.referenceUrl)
        ? proposal.referenceUrl.slice(0, 4000)
        : null,
      totalEstimateUsd: clampNumber(proposal.totalEstimateUsd, 0, 10_000, 0),
      balanceUsd: clampNumber(proposal.balanceUsd, 0, 1_000_000, 0),
      decided: proposal.decided === 'allowed' || proposal.decided === 'declined' ? proposal.decided : 'pending',
    }];
  });
}

/**
 * Validate/clamp an untrusted `transitionIn` value: an invalid `type` drops
 * the field entirely (like `cleanAudioTrack` dropping the whole track on a
 * missing `s3Key`); a present, valid `type` always survives with its
 * `durationSec` clamped into range (NaN-safe, mirrors `clampNumber`'s
 * fallback-on-invalid behavior rather than dropping the field).
 */
export function cleanEditorTransitionIn(value: unknown): EditorTransition | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Partial<EditorTransition>;
  if (typeof v.type !== 'string' || !TRANSITION_TYPES.includes(v.type as EditorTransition['type'])) return undefined;
  return { type: v.type as EditorTransition['type'], durationSec: clampTransitionDuration(Number(v.durationSec)) };
}

export function persistableEditorClips(clips: EditorTimelineClip[]) {
  return clips
    .filter((clip) => isDurableEditorUrl(clip.src))
    .map((clip) => {
      let normalized =
        clip.volume === undefined
          ? clip
          : { ...clip, volume: clampNumber(clip.volume, 0, 1, 1) };
      normalized = { ...normalized };
      const playbackRate = clipPlaybackRate(clip);
      const fit = clipFit(clip);
      const positionX = clipPositionX(clip);
      const positionY = clipPositionY(clip);
      const scale = clipScale(clip);
      const rotation = clipRotation(clip);
      const opacity = clipOpacity(clip);
      if (playbackRate !== 1) normalized.playbackRate = playbackRate;
      else delete normalized.playbackRate;
      if (fit !== 'contain') normalized.fit = fit;
      else delete normalized.fit;
      if (positionX !== 50) normalized.positionX = positionX;
      else delete normalized.positionX;
      if (positionY !== 50) normalized.positionY = positionY;
      else delete normalized.positionY;
      if (scale !== 1) normalized.scale = scale;
      else delete normalized.scale;
      if (rotation !== 0) normalized.rotation = rotation;
      else delete normalized.rotation;
      if (opacity !== 1) normalized.opacity = opacity;
      else delete normalized.opacity;
      const filterId = cleanEditorFilterId(clip.filterId);
      if (filterId !== 'none') normalized = { ...normalized, filterId };
      else if (normalized.filterId !== undefined) {
        normalized = { ...normalized };
        delete normalized.filterId;
      }
      const transitionIn = cleanEditorTransitionIn(clip.transitionIn);
      if (transitionIn) return { ...normalized, transitionIn };
      if (!clip.transitionIn) return normalized;
      const next = { ...normalized };
      delete next.transitionIn;
      return next;
    });
}

export function persistableEditorAssets(assets: EditorStickerAsset[]) {
  return assets.filter((asset) => isDurableEditorUrl(asset.src));
}

/**
 * Validate an untrusted `rotation` (degrees): a finite, non-zero value
 * survives as-is; NaN/Infinity/0/missing all collapse to `undefined` (the
 * unrotated default), mirroring `cleanEditorTransitionIn`'s "present+valid
 * survives, else drop the field" shape rather than clamping to a fallback.
 */
export function cleanLayerRotation(value: unknown): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  // Clamp magnitude (matches keyframe/clip rotation bounds) — an extreme value
  // otherwise flows unbounded into sharp().rotate / ffmpeg rotate expressions.
  return Math.max(-3600, Math.min(3600, n));
}

// Closed enums — these values are interpolated RAW into the render SVG
// (font-weight/style attributes AND the bundled @font-face <style> block), so
// an unvalidated string is an SVG/CSS injection into librsvg. Validate at the
// persistence boundary the same way transition/filter enums are.
const VALID_FONT_WEIGHTS = new Set(['400', '600', '700', '800']);
const VALID_FONT_STYLES = new Set(['normal', 'italic']);

// NaN-safe clamp (bounds match layerVisualSnapshot) — the base layer visual
// fields feed the no-keyframe render path (ffmpeg overlay-position expression),
// so an unfinite/out-of-range base value would emit "NaN" and break the export.
const clampFinite = (value: unknown, min: number, max: number, fallback: number) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

export function persistableEditorLayers(layers: EditorOverlayLayer[]) {
  return layers
    .filter((layer) => !layer.src || isDurableEditorUrl(layer.src))
    .map((layer) => {
      const rotation = cleanLayerRotation(layer.rotation);
      const transitionIn = cleanLayerTransition(layer.transitionIn);
      const transitionOut = cleanLayerTransition(layer.transitionOut);
      const next = { ...layer };
      next.x = clampFinite(layer.x, 0, 100, 50);
      next.y = clampFinite(layer.y, 0, 100, 50);
      next.size = clampFinite(layer.size, 1, 1200, 96);
      next.opacity = clampFinite(layer.opacity, 0, 1, 1);
      if (layer.widthPercent !== undefined) {
        next.widthPercent = clampFinite(layer.widthPercent, 1, 200, 26);
      }
      next.fontWeight = VALID_FONT_WEIGHTS.has(layer.fontWeight as string)
        ? layer.fontWeight
        : '800';
      next.fontStyle = VALID_FONT_STYLES.has(layer.fontStyle as string)
        ? layer.fontStyle
        : 'normal';
      next.role = layer.role === 'caption' ? 'caption' : undefined;
      if (next.role === undefined) delete next.role;
      if (rotation === undefined) delete next.rotation;
      else next.rotation = rotation;
      if (transitionIn) next.transitionIn = transitionIn;
      else delete next.transitionIn;
      if (transitionOut) next.transitionOut = transitionOut;
      else delete next.transitionOut;
      const keyframes = cleanLayerKeyframes(layer.keyframes, next);
      if (keyframes.length) next.keyframes = keyframes;
      else delete next.keyframes;
      return next;
    });
}

export function recalcEditorClipStarts(clips: EditorTimelineClip[]) {
  let cursor = 0;
  return clips.map((clip) => {
    const duration = cleanEditorDuration(clip.duration) || 0;
    const next = { ...clip, start: cursor, duration };
    cursor += duration;
    return next;
  });
}

export function sanitizeEditorProjectPayload(payload: Partial<EditorProjectSavePayload>) {
  const aspectRatio = cleanEditorAspectRatio(payload.aspectRatio);
  const clips = recalcEditorClipStarts(
    persistableEditorClips(cleanEditorArray<EditorTimelineClip>(payload.clips))
  );
  const duration =
    clips.length > 0
      ? clips.reduce((sum, clip) => sum + cleanEditorDuration(clip.duration), 0)
      : cleanEditorDuration(payload.duration);

  return {
    title: cleanEditorTitle(payload.title),
    status: 'draft' as const,
    aspectRatio,
    duration,
    clips,
    layers: persistableEditorLayers(cleanEditorArray<EditorOverlayLayer>(payload.layers)),
    assets: persistableEditorAssets(cleanEditorArray<EditorStickerAsset>(payload.assets)),
    audioTrack: cleanAudioTrack(payload.audioTrack),
    audioTracks: cleanAudioTracks(payload.audioTracks, payload.audioTrack),
    trackSettings: cleanTrackSettings(payload.trackSettings),
    crossfadeDuration: clampNumber(payload.crossfadeDuration, 0, 2, 0.3),
    genProposals: cleanGenProposals(payload.genProposals),
    filterId: cleanEditorFilterId(payload.filterId),
    modelId: typeof payload.modelId === 'string' ? payload.modelId : undefined,
  };
}

export function buildEditorRenderSpec({
  projectId,
  title,
  aspectRatio,
  duration,
  clips,
  layers,
  assets,
  audioTrack,
  audioTracks,
  trackSettings,
  crossfadeDuration,
  filterId,
  exportOptions,
}: {
  projectId: string;
  title: string;
  aspectRatio: EditorAspectRatio;
  duration: number;
  clips: EditorTimelineClip[];
  layers: EditorOverlayLayer[];
  assets: EditorStickerAsset[];
  audioTrack?: EditorAudioTrack;
  audioTracks?: EditorAudioTrack[];
  trackSettings?: import('@/types/editor').EditorTrackSettings;
  crossfadeDuration?: number;
  filterId?: string;
  exportOptions?: Partial<EditorExportOptions>;
}): EditorRenderSpec {
  const cleanAspect = cleanEditorAspectRatio(aspectRatio);
  const cleanClips = recalcEditorClipStarts(persistableEditorClips(clips));

  return {
    version: EDITOR_RENDER_VERSION,
    projectId,
    title: cleanEditorTitle(title),
    canvas: editorRenderCanvas(cleanAspect, exportOptions),
    duration: cleanClips.length
      ? cleanClips.reduce((sum, clip) => sum + cleanEditorDuration(clip.duration), 0)
      : cleanEditorDuration(duration),
    clips: cleanClips,
    layers: persistableEditorLayers(layers),
    assets: persistableEditorAssets(assets),
    audioTrack: cleanAudioTrack(audioTrack),
    audioTracks: cleanAudioTracks(audioTracks, audioTrack),
    trackSettings: cleanTrackSettings(trackSettings),
    crossfadeDuration: clampNumber(crossfadeDuration, 0, 2, 0.3),
    filterId: cleanEditorFilterId(filterId),
  };
}
